import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Sprout, Truck, Users, Building2, HeartHandshake, Leaf, ShieldCheck, Award, DollarSign, Mail, Megaphone, Recycle, MapPin, Smartphone, Lock } from "lucide-react";
import VideoSection from "@/components/VideoSection";
import Testimonials from "@/components/Testimonials";
import FeaturedFarms from "@/components/FeaturedFarms";
import Seo from "@/components/Seo";

const HERO_IMG = "https://images.unsplash.com/photo-1500382017468-9049fed747ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTF8MHwxfHNlYXJjaHwyfHxvcmdhbmljJTIwZmFybSUyMGxhbmRzY2FwZSUyMHN1bnJpc2V8ZW58MHx8fHwxNzgxNjIzNzIzfDA&ixlib=rb-4.1.0&q=85";
const DRIVER_IMG = "https://images.unsplash.com/photo-1563558055993-34e0a8d9322c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxzbWlsaW5nJTIwZGVsaXZlcnklMjBkcml2ZXIlMjBmYXJtJTIwYm94fGVufDB8fHx8MTc4MTYyMzcyM3ww&ixlib=rb-4.1.0&q=85";
const PRODUCE_IMG = "https://images.unsplash.com/photo-1576181456177-2b99ac0aa1ef?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxmcmVzaCUyMG9yZ2FuaWMlMjB2ZWdldGFibGVzJTIwZmFybWVycyUyMG1hcmtldHxlbnwwfHx8fDE3ODE2MjM3MjN8MA&ixlib=rb-4.1.0&q=85";

const Section = ({ children, className = "" }) => (
  <section className={`max-w-7xl mx-auto px-6 sm:px-12 ${className}`}>{children}</section>
);

const BenefitCard = ({ icon: Icon, title, items, testid, className = "" }) => (
  <div data-testid={testid} className={`bg-white border border-stone-200 p-8 rounded-md lift ${className}`}>
    <div className="w-11 h-11 rounded-full bg-emerald-800/10 flex items-center justify-center text-emerald-800">
      <Icon size={22} strokeWidth={1.5} />
    </div>
    <h3 className="mt-6 font-serif text-2xl text-stone-900">{title}</h3>
    <ul className="mt-5 space-y-3 font-sans text-stone-600 text-sm leading-relaxed">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-amber-700 shrink-0" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  </div>
);

export default function Landing() {
  return (
    <div>
      <Seo url="/" />
      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Organic farm at golden hour" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-stone-900/45 via-stone-900/30 to-stone-50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 pt-32 pb-44 sm:pt-40 sm:pb-56">
          <div className="text-[11px] uppercase tracking-[0.4em] text-amber-200 font-sans">A Farmers' Market, Reimagined</div>
          <h1 className="mt-6 font-serif text-5xl sm:text-6xl lg:text-7xl text-stone-50 leading-[1.05] max-w-4xl">
            Real food, real hobby farmers,<br />straight to your door.
          </h1>
          <p className="mt-8 font-sans text-stone-100/90 text-lg max-w-2xl">
            A trusted network connecting growers, drivers, families and wholesale buyers — for fresh, fair, and beautifully imperfect produce.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/marketplace">
              <Button data-testid="hero-cta-marketplace" className="bg-emerald-800 hover:bg-emerald-700 text-stone-50 px-7 py-6 rounded-md text-sm tracking-wide">
                Browse Marketplace
              </Button>
            </Link>
            <Link to="/login">
              <Button data-testid="hero-cta-join" variant="outline" className="bg-white/10 backdrop-blur border-stone-50/40 text-stone-50 hover:bg-white/20 px-7 py-6 rounded-md text-sm tracking-wide">
                Join the Network
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* BENEFITS BENTO */}
      <Section className="py-24">
        <div className="grid md:grid-cols-12 gap-6 items-start">
          <div className="md:col-span-5">
            <div className="text-[11px] uppercase tracking-[0.3em] text-emerald-800 font-sans font-semibold">Why Azure Gypsy</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-900 leading-tight">
              A network built around the people who grow your food.
            </h2>
            <p className="mt-6 font-sans text-stone-600 text-base leading-relaxed max-w-md">
              Every farm, every driver, every family on our platform is here for one reason — to keep food honest, local, and within reach.
            </p>
          </div>

          <div className="md:col-span-7 grid sm:grid-cols-2 gap-6">
            <BenefitCard
              testid="benefit-farmers"
              icon={Sprout}
              title="For Hobby Farmers"
              items={[
                "Sell direct, set your own payment system",
                "List sale, free, barter or wholesale items",
                "Give away mutation & deformed produce",
                "Broadcast newsletters to your buyers",
                "Verify once, sell with confidence",
              ]}
            />
            <BenefitCard
              testid="benefit-drivers"
              icon={Truck}
              title="For Drivers"
              items={[
                "Competitive pay on local routes",
                "100% of tips paid straight to you",
                "Cash out via PayPal or CashApp",
                "Ask hobby farmers for free food, with respect",
                "Performance ledger & feedback tracking",
              ]}
            />
            <BenefitCard
              testid="benefit-clients"
              icon={Users}
              title="For Clients"
              items={[
                "Members order for free across the marketplace",
                "Know if it's harvested or harvesting soon",
                "Refer friends, with hobby farmer confirmation",
                "Welcome certificate on sign-up",
                "Direct messaging with your hobby farmer",
              ]}
              className="sm:col-span-1"
            />
            <BenefitCard
              testid="benefit-wholesale"
              icon={Building2}
              title="For Wholesale"
              items={[
                "Bulk for stores, families, individuals",
                "Onboarding tracked by our admins",
                "Direct contracts with growers",
                "Dedicated state-level pickup routes",
                "Transparent ledger every order",
              ]}
            />
          </div>
        </div>
      </Section>

      {/* IMAGE STRIP */}
      <Section className="py-12">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="relative overflow-hidden rounded-md h-80 border border-stone-200">
            <img src={PRODUCE_IMG} alt="Fresh produce" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-stone-50">
              <div className="text-xs uppercase tracking-[0.3em]">Marketplace</div>
              <div className="font-serif text-3xl mt-2">From the field, this morning.</div>
            </div>
          </div>
          <div className="relative overflow-hidden rounded-md h-80 border border-stone-200">
            <img src={DRIVER_IMG} alt="Delivery driver" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-stone-900/70 via-stone-900/10 to-transparent" />
            <div className="absolute bottom-6 left-6 right-6 text-stone-50">
              <div className="text-xs uppercase tracking-[0.3em]">Drivers</div>
              <div className="font-serif text-3xl mt-2">Local pickup. Honest pay.</div>
            </div>
          </div>
        </div>
      </Section>

      {/* HOW IT WORKS */}
      <Section className="py-24">
        <div className="grid md:grid-cols-4 gap-10">
          {[
            { icon: Leaf, title: "Hobby farmers list", text: "Add produce, meats, eggs — even mutation produce for free or barter." },
            { icon: HeartHandshake, title: "You order", text: "Members order at no extra cost. Without a membership, modest service fees apply." },
            { icon: Truck, title: "Drivers deliver", text: "Local routes, live navigation, tip and rate at delivery." },
            { icon: Award, title: "Verified hobby farms", text: "ID-verified hobby farmers, real food from real neighbors." },
          ].map((s, i) => (
            <div key={i} className="border-l border-stone-300 pl-6">
              <div className="text-emerald-800"><s.icon size={28} strokeWidth={1.5} /></div>
              <div className="mt-4 font-serif text-2xl text-stone-900">{s.title}</div>
              <p className="mt-2 font-sans text-stone-600 text-sm leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* FEATURED FARMS */}
      <FeaturedFarms limit={6} />

      {/* SELL YOUR HARVEST — hobby farmer benefits pitch */}
      <section data-testid="sell-your-harvest" className="bg-stone-900 text-stone-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 py-20 sm:py-28">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
            <div className="lg:col-span-5">
              <div className="text-[11px] uppercase tracking-[0.4em] text-amber-300 font-semibold">
                For Hobby Farmers · Worldwide
              </div>
              <h2 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                Turn your harvest into a living income.
              </h2>
              <p className="mt-6 font-sans text-stone-300 text-base sm:text-lg leading-relaxed max-w-md">
                Hobby farmers from every country, every category — vegetables, meats, herbs, honey, baked goods, services, anything you make on your land. At your price, on your schedule. We bring you buyers, drivers, and marketing.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/login">
                  <Button data-testid="syh-cta-list" className="bg-amber-400 hover:bg-amber-300 text-stone-900 font-semibold px-7 py-6 rounded-md">
                    Start listing your farm
                  </Button>
                </Link>
                <Link to="/hobby-farmers">
                  <Button
                    variant="outline"
                    data-testid="syh-cta-meet"
                    className="bg-white/5 backdrop-blur border-stone-50/30 text-stone-50 hover:bg-white/10 px-7 py-6 rounded-md"
                  >
                    Meet the farms already listed
                  </Button>
                </Link>
              </div>
              <p className="mt-6 text-xs text-stone-400 font-sans">
                Free to list anywhere in the world. You pay your own taxes directly to your local government — we never touch them.
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                {[
                  {
                    icon: DollarSign,
                    title: "Sell direct, set your own price",
                    text: "Bring your own payment system (PayPal, Venmo, Zelle, Square, cash on pickup). Members order with no extra fee.",
                  },
                  {
                    icon: Recycle,
                    title: "Mutation & odd-shaped produce, welcome",
                    text: "List your imperfect peppers and gnarly carrots free or as barter. Zero waste, full pantry.",
                  },
                  {
                    icon: ShieldCheck,
                    title: "Verified once, trusted forever",
                    text: "Upload your ID one time. The Verified badge sits on every listing and every profile.",
                  },
                  {
                    icon: Megaphone,
                    title: "Marketing for free",
                    text: "Your farm shows up on the landing page, the hobby farmer directory, and weekly digests to every member.",
                  },
                  {
                    icon: Mail,
                    title: "Newsletters built in",
                    text: "Broadcast 'tomatoes ripening Friday' to your buyers. They get a push to their phone. You get sold-out crates.",
                  },
                  {
                    icon: Truck,
                    title: "Local drivers do the running",
                    text: "Verified drivers pick up at your farm and drop off at the client's door — live GPS, no phone tag.",
                  },
                  {
                    icon: MapPin,
                    title: "State-level pickup zones",
                    text: "We surface your listings to nearby buyers first. No cross-country logistics headaches.",
                  },
                  {
                    icon: Smartphone,
                    title: "Manage from your phone",
                    text: "Install Azure Gypsy Farms to your home screen. Add a listing, reply to a buyer, accept a wholesale order in 30 seconds.",
                  },
                ].map((b, i) => (
                  <div
                    key={i}
                    data-testid={`syh-benefit-${i}`}
                    className="group bg-stone-800/70 border border-stone-700 rounded-md p-5 sm:p-6 hover:bg-stone-800 hover:border-amber-300/40 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      <div className="shrink-0 w-10 h-10 rounded-full bg-amber-300/15 text-amber-300 flex items-center justify-center group-hover:bg-amber-300/25 transition-colors">
                        <b.icon size={18} strokeWidth={1.75} />
                      </div>
                      <div>
                        <div className="font-serif text-lg sm:text-xl text-stone-50 leading-tight">
                          {b.title}
                        </div>
                        <p className="mt-2 text-sm text-stone-300 font-sans leading-relaxed">
                          {b.text}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* MEMBERSHIP TEASER */}
      <Section className="pb-24">
        <div className="bg-stone-900 rounded-md p-10 sm:p-16 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">Members Order Free</div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-stone-50 leading-tight">
              $89.99 a year. <br />Free ordering, all year long.
            </h2>
            <p className="mt-5 font-sans text-stone-300 max-w-md leading-relaxed">
              Membership unlocks free ordering across the whole marketplace. Standard membership is $129.99/year; lock in our founders' promo while it lasts.
            </p>
            <Link to="/pricing">
              <Button data-testid="membership-cta" className="mt-8 bg-amber-700 hover:bg-amber-600 text-stone-50 rounded-md px-7 py-6">
                See Membership Details
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-md">
              <div className="text-xs uppercase tracking-[0.2em] text-amber-300 font-semibold">Promo</div>
              <div className="mt-3 font-serif text-4xl text-stone-50">$89.99</div>
              <div className="text-stone-400 text-xs mt-1">per year · free ordering</div>
            </div>
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-md">
              <div className="text-xs uppercase tracking-[0.2em] text-stone-400 font-semibold">Standard</div>
              <div className="mt-3 font-serif text-4xl text-stone-50">$129.99</div>
              <div className="text-stone-400 text-xs mt-1">per year · free ordering</div>
            </div>
            <div className="bg-stone-800 border border-stone-700 p-6 rounded-md col-span-2 flex items-center gap-4">
              <ShieldCheck className="text-emerald-300" size={28} />
              <div>
                <div className="font-serif text-xl text-stone-50">Real food, verified hobby farms</div>
                <div className="text-xs text-stone-400 mt-1">Every farm is ID-verified. Real growers, real food.</div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <VideoSection />
      <Testimonials limit={6} className="pb-24" />

      {/* ADMIN / STAFF PORTAL — bottom of landing page */}
      <Section className="pb-24">
        <div
          data-testid="landing-admin-portal-section"
          className="bg-stone-900 border border-stone-800 rounded-md p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6"
        >
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-full bg-emerald-900 flex items-center justify-center shrink-0">
              <Lock className="text-stone-50" size={20} />
            </span>
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-amber-300 font-semibold">
                Administrator Access
              </div>
              <h2 className="mt-2 font-serif text-2xl sm:text-3xl text-stone-50 leading-tight">
                Azure Gypsy Farms staff portal
              </h2>
              <p className="mt-2 text-sm font-sans text-stone-400 max-w-xl">
                Operations, support inbox, driver verifications, ledger and member oversight — staff sign-in only.
                All access is audit-logged and rate-limited.
              </p>
            </div>
          </div>
          <Link to="/admin/login">
            <Button
              data-testid="landing-admin-signin-btn"
              className="bg-amber-700 hover:bg-amber-600 text-stone-50 rounded-md px-7 py-6 font-sans"
            >
              Admin Sign In
            </Button>
          </Link>
        </div>
      </Section>
    </div>
  );
}
